import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Input,
    Modal,
    Popover,
    Select,
    Spin,
    Switch,
    message,
} from 'antd'
import {
    CheckOutlined,
    DownOutlined,
    FileTextOutlined,
    LoadingOutlined,
} from '@ant-design/icons'
import * as com from '../Common'
import {
    createDocumentTemplate,
    updateDocumentTemplate,
} from './documentTemplateApi'
import { useLoanOfficerLicenseStates } from './useLoanOfficerLicenseStates'
import {
    getEffectiveDocumentTemplateStates,
    resolveLicenseStateSelection,
    withAllLicenseStatesOption,
} from './documentTemplateStates'
import {
    DOCUMENT_TEMPLATE_INCOME_TYPE_OPTIONS,
    getDocumentTemplateIncomeTypeLabels,
    normalizeDocumentTemplateIncomeTypes,
} from './documentTemplateIncomeTypes'
import {
    DOCUMENT_TEMPLATE_LOAN_TYPE_OPTIONS,
    normalizeDocumentTemplateLoanTypes,
} from './documentTemplateLoanTypes'
import './DocumentTemplateRequestTools.css'

const LOAN_TYPE_OPTIONS = DOCUMENT_TEMPLATE_LOAN_TYPE_OPTIONS.map(
    (option) => option.value
)
const PURPOSE_OPTIONS = ['Purchase', 'Refinance', 'Cash-out']
export const DOCUMENT_TEMPLATE_SUGGESTION_LIMIT = 6

const normalizeLoanType = (value) => {
    const normalized = String(value || '').toLowerCase()
    if (normalized.includes('fha')) return 'FHA'
    if (normalized.includes('jumbo')) return 'Jumbo'
    if (normalized.includes('va')) return 'VA'
    if (normalized.includes('conventional')) return 'Conventional'
    return ''
}

const normalizePurpose = (value) => {
    const normalized = String(value || '').toLowerCase()
    if (normalized === 'purchase') return 'Purchase'
    if (normalized.includes('cashout')) return 'Cash-out'
    if (normalized.includes('refinance')) return 'Refinance'
    return ''
}

const normalizeState = (value) => {
    const normalized = String(value || '').trim()
    if (/^[A-Za-z]{2}$/.test(normalized)) return normalized.toUpperCase()
    const entry = Object.entries(com.mapAbbrFullName).find(
        ([, name]) => name.toLowerCase() === normalized.toLowerCase()
    )
    return entry ? entry[0] : ''
}

const getBorrowerIncomeTypes = (borrower) => {
    const occupation = borrower?.occupation
    let employmentIncomeType = ''
    if (occupation?.hasoccupation === 'retired') {
        employmentIncomeType = 'Retired'
    } else if (occupation?.selfemployed) {
        employmentIncomeType = 'Self-employed'
    } else if (occupation?.hasoccupation === 'employed') {
        employmentIncomeType = 'W-2'
    }

    return normalizeDocumentTemplateIncomeTypes([
        employmentIncomeType,
        ...(Array.isArray(borrower?.nonqm?.nonqmincometype)
            ? borrower.nonqm.nonqmincometype
            : []),
    ])
}

export const getDocumentTemplateLoanContext = (application) => ({
    loanType: normalizeLoanType(
        application?.loanOverview?.loanType ||
            application?.product?.mortgageapplied
    ),
    purpose: normalizePurpose(application?.property?.purpose),
    incomeTypes: getBorrowerIncomeTypes(application?.borrower),
    state: normalizeState(application?.property?.state),
})

const comparableRows = (rows) =>
    (rows || []).map((item) => ({
        name: String(item.name || '').trim(),
        category: item.category || '',
        timeframe: item.timeRange || item.timeframe || '',
        borrowerMessage: String(
            item.description || item.borrowerMessage || ''
        ).trim(),
    }))

export const documentTemplateRowsMatch = (rows, templateItems) =>
    JSON.stringify(comparableRows(rows)) ===
    JSON.stringify(comparableRows(templateItems))

export const templateItemsToTaskDocuments = (items) =>
    (items || []).map((item, index) => ({
        key: item.id || `document-template-row-${Date.now()}-${index}`,
        name: item.name || '',
        category: item.category || undefined,
        timeRange: item.timeframe || undefined,
        description: item.borrowerMessage || '',
    }))

const templateMatchesState = (template, state) =>
    Boolean(
        state &&
            (template.allStates || (template.licenseStates || []).includes(state))
    )

const TEMPLATE_MATCH_WEIGHTS = {
    state: 3,
    purpose: 2,
    loanType: 1,
    incomeType: 1,
}

const templateHasRecommendationTags = (template) =>
    Boolean(
        template.loanTypes?.length ||
            template.purpose ||
            template.incomeTypes?.length ||
            template.allStates ||
            (template.licenseStates || []).length
    )

const templateMatchScore = (template, context) => {
    let score = 0
    if (context.loanType && template.loanTypes?.includes(context.loanType)) {
        score += TEMPLATE_MATCH_WEIGHTS.loanType
    }
    if (context.purpose && template.purpose === context.purpose) {
        score += TEMPLATE_MATCH_WEIGHTS.purpose
    }
    if (
        context.incomeTypes?.some((incomeType) =>
            template.incomeTypes?.includes(incomeType)
        )
    ) {
        score += TEMPLATE_MATCH_WEIGHTS.incomeType
    }
    if (templateMatchesState(template, context.state)) {
        score += TEMPLATE_MATCH_WEIGHTS.state
    }
    return score
}

export const sortDocumentTemplatesForLoan = (templates, context) =>
    (templates || [])
        .filter(templateHasRecommendationTags)
        .sort((left, right) => {
            const scoreDifference =
                templateMatchScore(right, context) -
                templateMatchScore(left, context)
            if (scoreDifference !== 0) return scoreDifference
            if (left.isDefault !== right.isDefault) {
                return Number(right.isDefault) - Number(left.isDefault)
            }
            return left.name.localeCompare(right.name)
        })

export const getHighestMatchingDefaultDocumentTemplate = (
    templates,
    context
) =>
    sortDocumentTemplatesForLoan(templates, context).find(
        (template) => template.isDefault
    ) || null

const metadataLabel = (template, availableLicenseStates = []) =>
    [
        template.loanTypes?.join('/'),
        template.purpose,
        getDocumentTemplateIncomeTypeLabels(template.incomeTypes).join('/'),
        getEffectiveDocumentTemplateStates(
            template,
            availableLicenseStates
        ).join('/'),
    ]
        .filter(Boolean)
        .join(' \u00b7 ')

const contextLabel = (context) =>
    [context.purpose, context.state].filter(Boolean).join(' \u00b7 ') ||
    'this loan'

const TemplatePickerContent = ({
    applyingTemplateID,
    availableLicenseStates,
    context,
    error,
    loading,
    onApply,
    onBrowseAll,
    onRetry,
    templates,
}) => {
    const visibleTemplates = templates.slice(
        0,
        DOCUMENT_TEMPLATE_SUGGESTION_LIMIT
    )

    return (
        <div className="document-template-picker-panel">
            <div className="document-template-picker-heading">
                {`Suggested for ${contextLabel(context)}`}
            </div>
            {loading && (
                <div className="document-template-picker-state">
                    <Spin size="small" /> Loading templates...
                </div>
            )}
            {!loading && error && (
                <div className="document-template-picker-state document-template-picker-error">
                    <span>{error}</span>
                    <Button onClick={onRetry} size="small" type="link">
                        Retry
                    </Button>
                </div>
            )}
            {!loading && !error && visibleTemplates.length === 0 && (
                <div className="document-template-picker-state">
                    No active templates. Start with a blank request.
                </div>
            )}
            {!loading &&
                !error &&
                visibleTemplates.map((template) => (
                    <button
                        className="document-template-picker-option"
                        disabled={Boolean(applyingTemplateID)}
                        key={template.id}
                        onClick={() => onApply(template)}
                        type="button"
                    >
                        <span className="document-template-picker-option-copy">
                            <span className="document-template-picker-option-title">
                                {template.name}
                                {template.isDefault && (
                                    <span className="document-template-default-badge">
                                        DEFAULT
                                    </span>
                                )}
                            </span>
                            <span className="document-template-picker-option-meta">
                                {metadataLabel(
                                    template,
                                    availableLicenseStates
                                ) || 'No matching tags'}
                            </span>
                        </span>
                        <span className="document-template-picker-option-count">
                            {applyingTemplateID === template.id ? (
                                <LoadingOutlined />
                            ) : (
                                `${template.itemCount} items`
                            )}
                        </span>
                    </button>
                ))}
            <button
                className="document-template-browse-all"
                onClick={onBrowseAll}
                type="button"
            >
                Browse all templates
            </button>
        </div>
    )
}

export const DocumentTemplateStarter = ({
    appliedTemplate,
    applyingTemplateID,
    context,
    edited,
    error,
    loanState,
    loading,
    onApply,
    onBrowseAll,
    onChangeContextState,
    onClear,
    onRetry,
    templates,
}) => {
    const [pickerOpen, setPickerOpen] = useState(false)
    const {
        error: licenseStatesError,
        loading: licenseStatesLoading,
        states: availableLicenseStates,
    } = useLoanOfficerLicenseStates(true)
    const recommendationStateOptions = useMemo(
        () =>
            [loanState, ...availableLicenseStates]
                .filter(
                    (state, index, states) =>
                        Boolean(state) && states.indexOf(state) === index
                )
                .map((state) => ({
                    label: `${state} - ${com.mapAbbrFullName[state] || state}`,
                    value: state,
                })),
        [availableLicenseStates, loanState]
    )

    const pickerContent = (
        <TemplatePickerContent
            applyingTemplateID={applyingTemplateID}
            availableLicenseStates={availableLicenseStates}
            context={context}
            error={error}
            loading={loading}
            onApply={async (template) => {
                const applied = await onApply(template)
                if (applied !== false) setPickerOpen(false)
            }}
            onBrowseAll={() => {
                setPickerOpen(false)
                onBrowseAll()
            }}
            onRetry={onRetry}
            templates={templates}
        />
    )

    return (
        <div className="document-template-starter">
            {!appliedTemplate ? (
                <>
                    <span className="document-template-starter-icon">
                        <FileTextOutlined />
                    </span>
                    <div className="document-template-starter-copy">
                        <strong>Start from a template</strong>
                        <span>
                            Pre-fill the request below, then customize for this
                            borrower.
                        </span>
                    </div>
                    <Popover
                        content={pickerContent}
                        onOpenChange={setPickerOpen}
                        open={pickerOpen}
                        placement="bottomRight"
                        trigger="click"
                    >
                        <Button className="document-template-choose-button">
                            Choose a template <DownOutlined />
                        </Button>
                    </Popover>
                    <div className="document-template-context-select">
                        <Select
                            aria-label="Template recommendation state"
                            disabled={
                                licenseStatesLoading ||
                                Boolean(licenseStatesError)
                            }
                            popupMatchSelectWidth={220}
                            loading={licenseStatesLoading}
                            onChange={onChangeContextState}
                            optionFilterProp="label"
                            options={recommendationStateOptions}
                            placeholder="Select state"
                            showSearch
                            size="large"
                            value={context.state || undefined}
                        />
                    </div>
                </>
            ) : (
                <>
                    <span className="document-template-starter-icon document-template-starter-icon-applied">
                        <CheckOutlined />
                    </span>
                    <div className="document-template-starter-copy">
                        <strong>
                            Template applied {'\u00b7 '}
                            {appliedTemplate.name}
                            {edited && (
                                <span className="document-template-edited">
                                    {' \u00b7 edited'}
                                </span>
                            )}
                        </strong>
                        <span>
                            {metadataLabel(
                                appliedTemplate,
                                availableLicenseStates
                            ) || 'No matching tags'}
                        </span>
                    </div>
                    <Popover
                        content={pickerContent}
                        onOpenChange={setPickerOpen}
                        open={pickerOpen}
                        placement="bottomRight"
                        trigger="click"
                    >
                        <Button className="document-template-link-button" type="link">
                            Change
                        </Button>
                    </Popover>
                    <span className="document-template-action-divider" />
                    <Button
                        className="document-template-link-button document-template-clear-button"
                        onClick={onClear}
                        type="link"
                    >
                        Clear
                    </Button>
                </>
            )}
        </div>
    )
}

const initialMetadata = (appliedTemplate, context, availableStates) => ({
    name: appliedTemplate?.name || '',
    loanTypes: appliedTemplate
        ? normalizeDocumentTemplateLoanTypes(
              appliedTemplate.loanTypes || appliedTemplate.loanType
          )
        : normalizeDocumentTemplateLoanTypes(context.loanType),
    purpose: appliedTemplate?.purpose || context.purpose || '',
    incomeTypes: appliedTemplate
        ? normalizeDocumentTemplateIncomeTypes(appliedTemplate.incomeTypes)
        : normalizeDocumentTemplateIncomeTypes(context.incomeTypes),
    licenseStates: appliedTemplate
        ? getEffectiveDocumentTemplateStates(
              appliedTemplate,
              availableStates
          ).filter((state) => availableStates.includes(state))
        : availableStates.includes(context.state)
        ? [context.state]
        : [],
    isDefault: Boolean(appliedTemplate?.isDefault),
})

const SelectionChips = ({ multiple = false, onChange, options, value }) => (
    <div className="document-template-save-chips">
        {options.map((option) => {
            const selected = multiple
                ? value.includes(option)
                : value === option
            return (
                <button
                    aria-pressed={selected}
                    className={
                        'document-template-save-chip' +
                        (selected ? ' document-template-save-chip-selected' : '')
                    }
                    key={option}
                    onClick={() => onChange(option)}
                    type="button"
                >
                    {option}
                </button>
            )
        })}
    </div>
)

export const SaveDocumentTemplateModal = ({
    appliedTemplate,
    context,
    edited,
    items,
    onCancel,
    onSaved,
    open,
}) => {
    const {
        error: licenseStatesError,
        loading: licenseStatesLoading,
        options: licenseStateOptions,
        retry: retryLicenseStates,
        states: availableLicenseStates,
    } = useLoanOfficerLicenseStates(true)
    const [metadata, setMetadata] = useState(
        initialMetadata(appliedTemplate, context, availableLicenseStates)
    )
    const [errors, setErrors] = useState({})
    const [savingMode, setSavingMode] = useState('')

    useEffect(() => {
        if (!open) return
        setMetadata(
            initialMetadata(
                appliedTemplate,
                context,
                availableLicenseStates
            )
        )
        setErrors({})
        setSavingMode('')
    }, [appliedTemplate, availableLicenseStates, context, open])

    const canUpdate = Boolean(
        appliedTemplate?.id && appliedTemplate.canEdit && edited
    )
    const hasAnyTag = Boolean(
        metadata.loanTypes.length ||
            metadata.purpose ||
            metadata.incomeTypes.length ||
            metadata.licenseStates.length
    )
    const canBeDefault = Boolean(
        metadata.loanTypes.length &&
            metadata.purpose &&
            metadata.incomeTypes.length &&
            metadata.licenseStates.length
    )
    const defaultScope = [
        metadata.loanTypes.join('/'),
        metadata.purpose,
        getDocumentTemplateIncomeTypeLabels(metadata.incomeTypes).join('/'),
        metadata.licenseStates.join('/'),
    ]
        .filter(Boolean)
        .join(' \u00b7 ')

    const setSingleValue = (field, value) => {
        setMetadata((current) => ({
            ...current,
            [field]: current[field] === value ? '' : value,
        }))
        setErrors((current) => ({ ...current, tags: '' }))
    }

    const toggleMultipleValue = (field, value) => {
        setMetadata((current) => ({
            ...current,
            [field]: current[field].includes(value)
                ? current[field].filter((item) => item !== value)
                : [...current[field], value],
        }))
        setErrors((current) => ({ ...current, tags: '' }))
    }

    const changeStates = (states) => {
        const licenseStates = resolveLicenseStateSelection(
            states,
            availableLicenseStates
        )
        setMetadata((current) => ({
            ...current,
            licenseStates,
        }))
        setErrors((current) => ({ ...current, tags: '' }))
    }

    const saveTemplate = async (mode) => {
        const trimmedName = metadata.name.trim()
        const nextErrors = {
            name: !trimmedName ? 'Template name is required.' : '',
            tags: !hasAnyTag ? 'Select at least one template tag.' : '',
            defaultScope:
                metadata.isDefault && !canBeDefault
                    ? 'DEFAULT requires every tag and at least one state.'
                    : '',
        }
        if (
            mode === 'new' &&
            appliedTemplate &&
            trimmedName.toLowerCase() === appliedTemplate.name.toLowerCase()
        ) {
            nextErrors.name = 'Use a different name for the new template.'
        }
        setErrors(nextErrors)
        if (Object.values(nextErrors).some(Boolean)) return

        const payload = {
            name: trimmedName,
            loanTypes: metadata.loanTypes,
            purpose: metadata.purpose,
            incomeTypes: metadata.incomeTypes,
            allStates: false,
            licenseStates: metadata.licenseStates,
            isDefault: metadata.isDefault,
            items: comparableRows(items).map((item, index) => ({
                name: item.name,
                category: item.category,
                timeframe: item.timeframe,
                borrowerMessage: item.borrowerMessage,
                ownershipLevel: 'borrower',
                borrowerId: '',
                sortOrder: index,
            })),
        }

        setSavingMode(mode)
        try {
            const savedTemplate =
                mode === 'update'
                    ? await updateDocumentTemplate(appliedTemplate.id, payload)
                    : await createDocumentTemplate(payload)
            message.success(
                mode === 'update'
                    ? `"${savedTemplate.name}" updated.`
                    : `"${savedTemplate.name}" saved as a template.`
            )
            await onSaved(savedTemplate, mode)
        } catch (requestError) {
            message.error(
                requestError.message || 'Unable to save the template.'
            )
        } finally {
            setSavingMode('')
        }
    }

    return (
        <Modal
            centered
            className="document-template-save-modal"
            closable={!savingMode}
            footer={
                <div className="document-template-save-footer">
                    <div>
                        {canUpdate && (
                            <Button
                                disabled={Boolean(savingMode)}
                                loading={savingMode === 'new'}
                                onClick={() => saveTemplate('new')}
                            >
                                Save as new
                            </Button>
                        )}
                        <Button
                            loading={savingMode === (canUpdate ? 'update' : 'new')}
                            onClick={() =>
                                saveTemplate(canUpdate ? 'update' : 'new')
                            }
                            type="primary"
                        >
                            {canUpdate
                                ? `Update "${appliedTemplate.name}"`
                                : 'Save template'}
                        </Button>
                    </div>
                </div>
            }
            maskClosable={false}
            onCancel={onCancel}
            open={open}
            title="Save as template"
            width={520}
            zIndex={1100}
        >
            <div className="document-template-save-note">
                Saves the{' '}
                <strong>
                    {items.length} document row{items.length === 1 ? '' : 's'}
                </strong>{' '}
                (name, category, time frame, message). Borrower and due date
                stay per-loan and are not saved.
            </div>

            <label className="document-template-save-label" htmlFor="request-template-name">
                Template name
            </label>
            <Input
                id="request-template-name"
                maxLength={100}
                onChange={(event) => {
                    setMetadata((current) => ({
                        ...current,
                        name: event.target.value,
                    }))
                    setErrors((current) => ({ ...current, name: '' }))
                }}
                placeholder="e.g. Conventional Purchase - W-2"
                status={errors.name ? 'error' : ''}
                value={metadata.name}
            />
            {errors.name && (
                <div className="document-template-save-error">{errors.name}</div>
            )}

            <div className="document-template-save-grid">
                <div>
                    <div className="document-template-save-label">Loan type</div>
                    <SelectionChips
                        multiple
                        onChange={(value) =>
                            toggleMultipleValue('loanTypes', value)
                        }
                        options={LOAN_TYPE_OPTIONS}
                        value={metadata.loanTypes}
                    />
                </div>
                <div>
                    <div className="document-template-save-label">Purpose</div>
                    <SelectionChips
                        onChange={(value) => setSingleValue('purpose', value)}
                        options={PURPOSE_OPTIONS}
                        value={metadata.purpose}
                    />
                </div>
                <div>
                    <div className="document-template-save-label">Income type</div>
                    <Select
                        allowClear
                        className="document-template-license-state-select"
                        maxTagCount="responsive"
                        mode="multiple"
                        onChange={(incomeTypes) => {
                            setMetadata((current) => ({
                                ...current,
                                incomeTypes,
                            }))
                            setErrors((current) => ({
                                ...current,
                                tags: '',
                            }))
                        }}
                        optionFilterProp="label"
                        options={DOCUMENT_TEMPLATE_INCOME_TYPE_OPTIONS}
                        placeholder="Select income types"
                        showSearch
                        value={metadata.incomeTypes}
                    />
                </div>
                <div>
                    <div className="document-template-save-label">
                        License state(s) <span>your licensed states</span>
                    </div>
                    <Select
                        allowClear
                        className="document-template-license-state-select"
                        disabled={
                            licenseStatesLoading || Boolean(licenseStatesError)
                        }
                        loading={licenseStatesLoading}
                        maxTagCount="responsive"
                        mode="multiple"
                        onChange={changeStates}
                        optionFilterProp="label"
                        options={withAllLicenseStatesOption(licenseStateOptions)}
                        placeholder="Select licensed states"
                        showSearch
                        value={metadata.licenseStates}
                    />
                    {licenseStatesError && (
                        <div className="document-template-save-error">
                            <span>{licenseStatesError}</span>
                            <Button onClick={retryLicenseStates} size="small" type="link">
                                Retry
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            {errors.tags && (
                <div className="document-template-save-error">{errors.tags}</div>
            )}

            <div className="document-template-default-row">
                <Switch
                    checked={metadata.isDefault}
                    disabled={!metadata.isDefault && !canBeDefault}
                    onChange={(checked) =>
                        setMetadata((current) => ({
                            ...current,
                            isDefault: checked,
                        }))
                    }
                />
                <div>
                    <strong>Set as default for matching loans</strong>
                    <span>
                        {canBeDefault
                            ? `Auto-suggested first for ${defaultScope}.`
                            : 'Requires loan type, purpose, income type, and at least one state.'}
                    </span>
                </div>
            </div>
            {errors.defaultScope && (
                <div className="document-template-save-error">
                    {errors.defaultScope}
                </div>
            )}
        </Modal>
    )
}
